import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import {
  Button,
  Dialog,
  Grid,
  IconButton,
  Input,
  SnackBar,
  Typography,
} from 'components/atoms';
import { severityType } from 'components/atoms/Alert/Alert.styles';
import { Table } from 'components/organisms';
import { DELETE_USER, UPDATE_USER_STATE } from 'const';
import { useDialog, useGetTable } from 'hooks';
import useTable, { filterModelType, tableModelType } from 'hooks/useTable';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IoAdd } from 'react-icons/io5';
import { PaginationType, User } from 'types';
import { createFilterStructure } from 'utils';
import {
  defaultListUserTemplateFilter,
  defaultPagination,
  defaultSortModel,
  fetchProcess,
  getColumns,
} from './ListUserTemplate.const';
import {
  ListUserTemplateFilterSchema,
  ListUserTemplateFilterType,
} from './ListUserTemplate.schema';
import { ListUserTemplateProps } from './ListUserTemplate.types';

const ListUserTemplate = ({ ...props }: ListUserTemplateProps) => {
  const [loadingForm, setLoadingForm] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ListUserTemplateFilterType>({
    defaultValues: defaultListUserTemplateFilter,
    resolver: yupResolver(ListUserTemplateFilterSchema),
  });

  const [selected, onOpen, onCloseModal] = useDialog<number | undefined>(
    undefined,
  );
  const [action, setAction] = useState<'remove' | 'edit' | undefined>(
    undefined,
  );
  const [open, setOpen] = useState<severityType>();

  const confirmEdit = useCallback(
    (id: number) => {
      onOpen(id);
      setAction('edit');
    },
    [onOpen],
  );

  const confirmRemove = useCallback(
    (id: number) => {
      onOpen(id);
      setAction('remove');
    },
    [onOpen],
  );

  const onCloseSnackBar = useCallback(() => {
    setAction(undefined);
    setOpen(undefined);
  }, []);

  const onClose = useCallback(() => {
    onCloseModal();
  }, [onCloseModal]);

  const columns = useMemo(
    () => getColumns(confirmRemove, confirmEdit),
    [confirmEdit, confirmRemove],
  );

  const [filterModel, setFilterModel] = useState<filterModelType<User>[]>([]);

  const {
    loading,
    error,
    data: process,
    handleData,
    getData,
    mutate,
  } = useGetTable<PaginationType<User>, tableModelType<User>>(fetchProcess, {
    paginationModel: defaultPagination,
    sortModel: defaultSortModel,
    filterModel,
  });

  const tableControllers = useTable<User>(
    {
      filterModel,
      paginationModel: process
        ? { ...defaultPagination, rowsCount: process.rowsCount }
        : defaultPagination,
    },
    handleData,
    getData as (
      param: Omit<tableModelType<User>, 'paginationModel'>,
    ) => Promise<PaginationType<User>>,
  );

  const removeUser = async (idUser: number) => {
    try {
      setLoadingForm(true);
      await axios.delete(DELETE_USER.replace(':id', idUser.toString()));
      await handleData({
        paginationModel: tableControllers.paginationModel,
        sortModel: tableControllers.sortModel,
        filterModel,
      });
      onClose();
      setLoadingForm(false);
      setOpen('success');
    } catch (error) {
      onClose();
      setLoadingForm(false);
      setOpen('error');
    }
  };

  const editUser = async (idUser: number) => {
    try {
      setLoadingForm(true);

      await axios.put(UPDATE_USER_STATE.replace(':id', idUser.toString()));
      onClose();
      mutate((before) => {
        const result = [...before.result];
        const index = result.findIndex(({ id }) => id === idUser);
        result[index].enabled = !result[index].enabled;
        return { ...before, result };
      });
      setLoadingForm(false);
      setOpen('success');
    } catch (error) {
      onClose();
      setLoadingForm(false);
      setOpen('error');
    }
  };

  const onSubmit = useCallback(
    (filterListUserTemplate: ListUserTemplateFilterType) => {
      const filterUsername = createFilterStructure(
        'username',
        'contains',
        filterListUserTemplate.username,
      );

      const modelFilter = [filterUsername].filter((item) => item);
      setFilterModel(modelFilter as filterModelType<User>[]);
    },
    [],
  );

  const selectedUser = useMemo(
    () =>
      selected !== undefined
        ? process?.result.find((user) => user.id === selected)
        : undefined,
    [selected, process],
  );

  return (
    <>
      <Table<User>
        {...tableControllers}
        numberOfVisibleColumns={4}
        title={`Encontramos ${tableControllers.paginationModel.rowsCount} usuarios`}
        columns={columns}
        rows={process?.result || []}
        error={!!error}
        loading={loading}
        filterComponent={
          <Grid
            container
            alignItems="center"
            justify="flex-end"
            spacing={4}
            wrap="nowrap"
          >
            <Grid item>
              <IconButton
                info="Crear usuario"
                variant="contained"
                href="/pages/management/user/create"
              >
                <IoAdd />
              </IconButton>
            </Grid>
            <form noValidate onSubmit={handleSubmit(onSubmit)}>
              <Grid item>
                <Grid
                  container
                  alignItems="center"
                  justify="flex-end"
                  spacing={4}
                  wrap="nowrap"
                >
                  <Grid item xs={10}>
                    <Input
                      fullWidth
                      placeholder="Busca por nombre"
                      {...register('username')}
                      error={!!errors.username}
                      helperText={errors.username && errors.username.message}
                    />
                  </Grid>
                  <Grid item>
                    <Button
                      isSmall
                      type="submit"
                      variant="contained"
                      color="primary"
                    >
                      Filtrar
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Grid>
        }
      />
      <Dialog
        open={selected !== undefined}
        onClose={onClose}
        title="Confirma esta acción"
      >
        <Grid container spacing={3} justify="flex-end">
          <Grid item xs={12}>
            {action === 'remove' && (
              <Typography variant="h2">
                {`¿Estas seguro de eliminar el usuario ${selectedUser?.username}?`}
              </Typography>
            )}
            {action === 'edit' && (
              <Typography variant="h2">
                {`¿Estas seguro de editar el estado del usuario ${selectedUser?.username}?`}
              </Typography>
            )}
          </Grid>
          <Grid item>
            <Button variant="contained" color="grey" onClick={onClose}>
              Cancelar
            </Button>
          </Grid>
          <Grid item>
            {action === 'remove' && (
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  removeUser(selectedUser?.id ? selectedUser?.id : 0)
                }
                loading={loadingForm}
              >
                Si, deseo eliminarlo
              </Button>
            )}

            {action === 'edit' && (
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  editUser(selectedUser?.id ? selectedUser?.id : 0)
                }
                loading={loadingForm}
              >
                Si, deseo modificarlo
              </Button>
            )}
          </Grid>
        </Grid>
      </Dialog>
      <SnackBar
        wait={2000}
        open={open !== undefined}
        onClose={onCloseSnackBar}
        severity={open}
      >
        {open === 'success' &&
          action === 'edit' &&
          'Modificamos el estado del usuario exitosamente'}
        {open === 'success' &&
          action === 'remove' &&
          'Eliminamos el usuarios exitosamente'}
        {open === 'error' && 'Ocurrió un error, intenta nuevamente'}
      </SnackBar>
    </>
  );
};

export default ListUserTemplate;
